import React from 'react'
import { Heading, Text } from '@primer/react-brand'
import {
  ListUnorderedIcon,
  SearchIcon,
  GraphIcon,
  SparkleFillIcon,
  ProjectIcon,
  PeopleIcon,
  BellIcon,
  PlugIcon,
} from '@primer/octicons-react'
import Container from './Container'
import SectionHeader from './landing/SectionHeader'

// Eight capabilities on a twelve-column grid the section controls itself
// (Primer's Bento stretched every row to its tallest tile and left the small
// ones half empty). The two that define the product — search across
// everything, and taking the data out — are the wide anchors and carry a
// monospace detail line that earns the width; the other six sit at equal
// weight in two rows of three, so no tile is ever alone on a row.
const ANCHORS = [
  {
    icon: SearchIcon,
    heading: 'Search, filter and sort everything collected',
    body:
      'Boolean search across every post, transcript and extracted image text. The same index ' +
      'behind the whole product.',
    detail: ['platform', 'date', 'engagement', 'media type', 'transcript', 'your lists', 'any attribute'],
  },
  {
    icon: PlugIcon,
    heading: 'Your data, your tools',
    body:
      'Take it out and use what you already have — spreadsheets, notebooks, your own pipeline or ' +
      'your own agent. Nothing is locked in.',
    detail: ['CSV export', 'REST API', 'MCP server for your own AI agent'],
  },
]

const FEATURES = [
  { icon: ListUnorderedIcon, heading: 'Lists', body: 'Decide exactly what you look at, and share it with your team.' },
  { icon: GraphIcon, heading: 'Stats over time', body: 'How volume and engagement move for an account, a term or a whole list — and what is outperforming.' },
  { icon: PeopleIcon, heading: 'Actors and actor sets', body: 'Accounts tied to the real people and organisations behind them, with attributes you can filter on.' },
  { icon: ProjectIcon, heading: 'Dashboards', body: 'A shareable, interactive view of the content and insights that matter for a story or a report.' },
  { icon: SparkleFillIcon, heading: 'AI assistant', body: 'Ask questions of the content you track in plain language; answers are grounded in the actual posts.' },
  { icon: BellIcon, heading: 'Alerts', body: 'Be told when the accounts or terms you follow do something worth looking at.' },
]

const IconBadge = ({ icon: Icon }) => (
  <span
    aria-hidden="true"
    style={{
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      borderRadius: '10px',
      background: 'var(--base-color-scale-blue-0)',
      color: 'var(--base-color-scale-blue-6)',
      flexShrink: 0,
    }}
  >
    <Icon size={20} />
  </span>
)

const tileStyle = {
  display: 'flex',
  flexDirection: 'column',
  gap: '14px',
  padding: '24px',
  borderRadius: '12px',
  border: '1px solid var(--brand-color-border-default)',
  background: 'var(--brand-color-canvas-default)',
  boxSizing: 'border-box',
  minWidth: 0,
}

const Tile = ({ icon, heading, body, detail, wide }) => (
  <div className={wide ? 'feature-wide' : 'feature-small'} style={{ ...tileStyle, gridColumn: wide ? 'span 6' : 'span 4' }}>
    {/* Badge beside the text, not above it: a 20px glyph is a marker, not an
        illustration, and the row layout saves a line per tile. */}
    <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
      <IconBadge icon={icon} />
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', minWidth: 0, flex: 1 }}>
        <Heading as="h3" size={wide ? '5' : '6'} weight="semibold" style={{ textWrap: 'balance', marginTop: wide ? '2px' : '8px' }}>
          {heading}
        </Heading>
        <Text as="p" size="200" variant="muted" style={{ margin: 0 }}>
          {body}
        </Text>
      </div>
    </div>
    {detail && (
      <ul
        style={{
          listStyle: 'none',
          // Aligns with the text column: 40px badge + 16px gap.
          margin: '0 0 0 56px',
          padding: '14px 0 0',
          borderTop: '1px solid var(--brand-color-border-muted)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px 14px',
          fontFamily: 'var(--brand-fontStack-monospace)',
          fontSize: '12px',
          letterSpacing: '0.04em',
          color: 'var(--brand-color-text-muted)',
        }}
      >
        {detail.map((d) => <li key={d}>{d}</li>)}
      </ul>
    )}
  </div>
)

const FeaturesSection = () => {
  return (
    <section id="features" style={{ padding: '96px 0', background: 'var(--brand-color-canvas-subtle)' }}>
      <Container>
        <SectionHeader
          eyebrow="What you can do"
          heading="One place to follow the conversation, understand it, and take what you learn with you."
        />

        <div
          className="features-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
            gap: '16px',
            marginTop: '56px',
          }}
        >
          {ANCHORS.map((f) => <Tile key={f.heading} wide {...f} />)}
          {FEATURES.map((f) => <Tile key={f.heading} {...f} />)}
        </div>
      </Container>
    </section>
  )
}

export default FeaturesSection
